import * as React from "react";

import {
  MIN_ENTRANCE_YEAR,
  MAX_ENTRANCE_YEAR,
  MIN_DELIVERY_YEAR,
  MAX_DELIVERY_YEAR,
} from "../Definition";
import { IStudent } from "../state";
const Chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

export function is_develop() {
  return (
    process.env.REACT_APP_MANAGER_APP_URL?.startsWith("http://localhost") ||
    process.env.REACT_APP_MANAGER_APP_URL?.startsWith("https://dev-")
  );
}

export function delimitize(num: number) {
  return String(num).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
}

export function zeroPadding(num: number, padding = 2) {
  const paddingStr = Array.from(Array(padding), () => 0).join("");
  return (paddingStr + num.toString()).slice(-1 * padding);
}

export function toYearMonth(yearMonth: string) {
  const [year, month] = yearMonth.split("-");
  return { year: parseInt(year, 10), month: parseInt(month, 10) };
}

export function formatYearMonth(year: number, month: number) {
  return `${year}-${zeroPadding(month)}`;
}

export function isSelectableDeliveryYearMonth(yearMonth: string) {
  if (!/^\d{4}-\d{2}$/.test(yearMonth)) {
    return false;
  }
  const { year, month } = toYearMonth(yearMonth);
  return (
    MIN_DELIVERY_YEAR <= year &&
    year <= MAX_DELIVERY_YEAR &&
    1 <= month &&
    month <= 12
  );
}

export function randomStr(n: number) {
  let s = "";
  while (n > 0) {
    s += Chars[Math.floor(Math.random() * Chars.length)];
    n -= 1;
  }
  return s;
}

export function uniqueStr() {
  const now = new Date();
  return (
    now.getFullYear() +
    zeroPadding(now.getMonth() + 1) +
    zeroPadding(now.getDate()) +
    randomStr(6)
  );
}

// date/month入力はカレンダーアイコンをクリックしないとピッカーが開かないため、欄のどこを押しても開くようにする
export function openDatePicker(e: React.MouseEvent<HTMLInputElement>) {
  const input = e.currentTarget as HTMLInputElement & {
    showPicker?: () => void;
  };
  if (typeof input.showPicker === "function") {
    try {
      input.showPicker();
    } catch (_e) {
      // ユーザー操作と見なされない場合などはネイティブ挙動に任せる
    }
  }
}

export function thisSchoolYear() {
  return new Date().getMonth() < 3
    ? new Date().getFullYear() - 1
    : new Date().getFullYear();
}

export function formatGrade(year: number) {
  const currentYear = thisSchoolYear();
  if (year === 0) {
    return "未定";
  } else if (year === currentYear - 2) {
    return year + "(3年生)";
  } else if (year === currentYear - 1) {
    return year + "(2年生)";
  } else if (year === currentYear) {
    return year + "(1年生)";
  } else if (year >= currentYear) {
    return year + "(入学生)";
  }
  return year + "(卒業生)";
}

export function strToTag(child: any) {
  if (typeof child !== "string") {
    return;
  }
  const ret: any = [];
  let str = "";
  child.split("").forEach((n: string, at: number) => {
    if (n === " ") {
      if (str !== "") {
        ret.push(<span key={`s_${at}`}>{str}</span>);
        str = "";
      }
      ret.push(<span key={`b_${at}`}>&nbsp;</span>);
    } else if (n === "\n") {
      if (str !== "") {
        ret.push(
          <span key={`s_${at}`}>
            {str}
            <br />
          </span>,
        );
        str = "";
      } else {
        ret.push(
          <span key={`b_${at}`}>
            <br />
          </span>,
        );
      }
    } else {
      str += n;
    }
  });
  if (str !== "") {
    ret.push(<span key={`s_last`}>{str}</span>);
  }
  return ret;
}

export function timeStr(date: Date | undefined | null) {
  if (!date) return "";
  return zeroPadding(date.getHours()) + ":" + zeroPadding(date.getMinutes());
}

export function addDate(date: Date, num: number) {
  return new Date(
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() +
      1000 * 60 * 60 * 24 * num,
  );
}

export function age(birthday: Date) {
  const today = new Date();
  let bias = 0;
  if (
    zeroPadding(today.getMonth()) + zeroPadding(today.getDate()) <
    zeroPadding(birthday.getMonth()) + zeroPadding(birthday.getDate())
  ) {
    bias = -1;
  }
  return today.getFullYear() - birthday.getFullYear() + bias;
}

export function convertUidToUuid(uid: string) {
  return (
    uid.slice(0, 8) +
    "-0000-4000-8000-" +
    uid
      .slice(8)
      .split("")
      .map(c => c.charCodeAt(0).toString(16))
      .join("")
  );
}

export function dateStr(date: Date | undefined | null, withoutYear?: boolean) {
  if (!date) return "";
  return (
    (withoutYear ? "" : date.getFullYear().toString() + "年") +
    (date.getMonth() + 1) +
    "月" +
    date.getDate() +
    "日"
  );
}

export function monthTimeStr(date: Date | undefined | null) {
  if (!date) return "";
  return date.getMonth() + 1 + "月" + date.getDate() + "日 " + timeStr(date);
}

export function dToS(date: Date) {
  return (
    date.getFullYear().toString() +
    "-" +
    zeroPadding(date.getMonth() + 1) +
    "-" +
    zeroPadding(date.getDate())
  );
}

export function formatTimeStr(date: Date) {
  return dToS(date) + "T" + timeStr(date) + "+09:00";
}

export function dayStr(date: Date | undefined | null) {
  if (!date) return "";
  return ["日", "月", "火", "水", "木", "金", "土"][date.getDay()];
}

export function dateTimeStr(
  date: Date | undefined | null,
  withoutYear?: boolean,
) {
  if (!date) return "";
  return dateStr(date, withoutYear) + "(" + dayStr(date) + ") " + timeStr(date);
}

export function simpleDateTimeStr(date: Date) {
  return dateStr(date, true) + timeStr(date);
}

export function buildDate(date: Date | string) {
  if (typeof date === "string") {
    return new Date(date.replace(" +0900", "+09:00").replace(" ", "T"));
  } else {
    return new Date(date);
  }
}

export function fetchIDFromPath() {
  const id = window.location.pathname.split("/").pop() as string;
  return Number(id);
}

export function parseQueryParams() {
  const result: { [key: string]: any } = {};
  if (!window.location.href.match(/\?/)) {
    return result;
  }
  const query = window.location.href.replace(/.*\?/, "").split("&");
  query.forEach(r => {
    const pair = r.split("=");
    const val = unescape(pair[1]);
    if (pair[0].match(/\[\]$/)) {
      const key = pair[0].replace("[]", "");
      if (!result[key]) {
        result[key] = [];
      }
      if (val !== "") {
        result[key].push(val);
      }
    } else {
      result[pair[0]] = val;
    }
  });
  return result;
}

/**
 * Check if two arrays are equal
 * @param  {Array}   arr1 The first array
 * @param  {Array}   arr2 The second array
 * @return {Array}   different element array
 */
function arraysMissMatch(arr1: any[], arr2: any[]) {
  const arr = [];
  // Check if all items exist and are in the same order
  for (let i = 0; i < arr1.length; i++) {
    const d = diff(arr1[i], arr2[i]);
    if (typeof d !== "object" || Object.keys(d).length > 0) {
      arr.push(d);
    }
  }
  if (arr1.length < arr2.length) {
    for (let i = arr1.length; i < arr2.length; i++) {
      arr.push(arr2[i]);
    }
  }
  // Otherwise, return true
  return arr;
}

/**
 * Compare two items and push non-matches to object
 * @param  {*}      item1 The first item
 * @param  {*}      item2 The second item
 * @param  {String} key   The key in our object
 */
function compare(item1: any, item2: any, key: string) {
  const type1 = Object.prototype.toString.call(item1);
  const type2 = Object.prototype.toString.call(item2);
  const diffs: { [key: string]: any } = {};

  if (type1 !== type2) {
    diffs[key] = item2;
    return diffs;
  }

  if (item1 instanceof Date && item2 instanceof Date) {
    if (item1.getTime() !== item2.getTime()) {
      diffs[key] = item2;
    }
    return diffs;
  }

  if (type1 === "[object Object]") {
    const objDiff = diff(item1, item2);
    if (typeof objDiff !== "object" || Object.keys(objDiff).length > 0) {
      diffs[key] = objDiff;
    }
    return diffs;
  }

  if (type1 === "[object Array]") {
    const missMatch = arraysMissMatch(item1, item2);
    if (missMatch.length > 0) {
      diffs[key] = missMatch;
    }
    return diffs;
  }

  if (type1 === "[object Function]") {
    if (item1.toString() !== item2.toString()) {
      diffs[key] = item2;
    }
    return diffs;
  }

  if (item1 !== item2) {
    diffs[key] = item2;
  }
  return diffs;
}

export function diff(obj1: any, obj2: any) {
  let diffs: { [key: string]: any } = {};
  let key: string;

  if (obj1 === obj2) {
    return diffs;
  }
  if (typeof obj1 === "number" && typeof obj2 === "number") {
    if (obj1 !== obj2) {
      return obj2;
    }
    return diffs;
  }
  if (typeof obj1 === "string" && typeof obj2 === "string") {
    if (obj1 !== obj2) {
      return obj2;
    }
    return diffs;
  }
  if (typeof obj1 === "undefined" || obj1 === null) {
    if (typeof obj2 === "undefined" || obj2 === null) {
      return diffs;
    }
    return obj2;
  }

  if (Object.prototype.toString.call(obj1) === "[object Function]") {
    if (obj1.toString() === obj2.toString()) {
      return diffs;
    }
    return obj2;
  }

  if (obj1 instanceof Date && obj2 instanceof Date) {
    if (obj1.getTime() !== obj2.getTime()) {
      return obj2;
    }
    return diffs;
  }

  for (key in obj1) {
    if (obj1.hasOwnProperty(key)) {
      if (obj2) {
        diffs = { ...diffs, ...compare(obj1[key], obj2[key], key) };
      } else {
        diffs[key] = undefined;
      }
    }
  }
  for (key in obj2) {
    if (obj2.hasOwnProperty(key)) {
      if (!obj1.hasOwnProperty(key)) {
        diffs[key] = obj2[key];
      }
    }
  }
  return diffs;
}

export function convertLink(content: string) {
  return content
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(
      /\[([^\]]*)\]\(([^)]*)\)/g,
      (_: string, text: string, url: string) => {
        if (!/^https?:\/\//i.test(url)) return text;
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      },
    );
}

export function setCookie(cname: string, cvalue: string, hours: number) {
  const d = new Date();
  d.setTime(d.getTime() + hours * 60 * 60 * 1000);
  const expires = "expires=" + d.toUTCString();
  document.cookie =
    cname + "=" + cvalue + ";" + expires + ";domain=.enageed.net;path=/";
}

export function deleteAllCookies() {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie =
      name +
      "=;domain=.enageed.net;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT";
  }
}

export const formatDate = (
  date: string,
  originDelimiter = "-",
  delimiter = ".",
) => date.split(originDelimiter).join(delimiter);

export const range = (start = 0, stop = 0, step = 1) =>
  Array.from(
    { length: step ? (stop - start) / step + 1 : 0 },
    (_, i) => start + i * step,
  );

export enum EStudentSortType {
  id = "ID",
  identity = "ログインID",
  student_no_in_school = "学籍番号",
  full_name_kana = "名前（カナ）",
  class_and_student_no_in_school = "クラス＆学籍番号",
  class_and_full_name_kana = "クラス＆名前（カナ）",
}

export function SortStudents(students: IStudent[], sortBy: EStudentSortType) {
  const collator = new Intl.Collator(["ja", "en"]);

  switch (sortBy) {
    case EStudentSortType.identity:
      students.sort((a, b) => collator.compare(a.identity, b.identity));
      break;
    case EStudentSortType.student_no_in_school:
      students.sort((a, b) => {
        if (!a.student_no_in_school) return 1;
        if (!b.student_no_in_school) return -1;
        return collator.compare(a.student_no_in_school, b.student_no_in_school);
      });
      break;
    case EStudentSortType.full_name_kana:
      students.sort((a, b) =>
        collator.compare(
          `${a.last_name_kana} ${a.first_name_kana}`,
          `${b.last_name_kana} ${b.first_name_kana}`,
        ),
      );
      break;
    case EStudentSortType.class_and_student_no_in_school:
      students.sort((a, b) => {
        if (a.class_id === b.class_id) {
          if (!a.student_no_in_school) return 1;
          if (!b.student_no_in_school) return -1;
          return collator.compare(
            a.student_no_in_school,
            b.student_no_in_school,
          );
        }
        if (!a.class_id) return 1;
        if (!b.class_id) return -1;
        return a.class_id - b.class_id;
      });
      break;
    case EStudentSortType.class_and_full_name_kana:
      students.sort((a, b) => {
        if (a.class_id === b.class_id) {
          return collator.compare(
            `${a.last_name_kana} ${a.first_name_kana}`,
            `${b.last_name_kana} ${b.first_name_kana}`,
          );
        }
        if (!a.class_id) return 1;
        if (!b.class_id) return -1;
        return a.class_id - b.class_id;
      });
      break;
    case EStudentSortType.id:
    default:
      students.sort((a, b) => a.id - b.id);
  }
}

export const getOptionsSchoolTeachingMaterial = () => {
  return Array.from(
    { length: MAX_ENTRANCE_YEAR - MIN_ENTRANCE_YEAR + 1 },
    (_, i) => {
      return {
        value: MIN_ENTRANCE_YEAR + i,
        label: `${MIN_ENTRANCE_YEAR + i}年度`,
      };
    },
  );
};
